import { FullscreenExitOutlined, FullscreenOutlined, MailOutlined } from "@ant-design/icons";
import DirectoryUserPreview from "@components/DirectoryUserPreview";
import { FilterBar, FilterRow } from "@components/FilterBar";
import FocusOverlay from "@components/FocusOverlay";
import MailModal from "@components/MailModal";
import ObjectSelect, { type ObjectSelectValue } from "@components/ObjectSelect";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import {
  type AdminUser,
  type AdminUserFilters,
  useAdminUsers,
  useCreateAdminUser,
  useDeleteDirectoryUser,
  useImportOracleUsers,
} from "@features/users/api";
import { getPrimaryContact } from "@shared/directoryUsers";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import DrawerForm from "@shared/ui/drawer-form";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useStandardPagination } from "@shared/useStandardPagination";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  ConfigProvider,
  Form,
  Input,
  Popconfirm,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

interface CreateUserFormValues {
  id: string;
  last_name?: string;
  first_name?: string;
  middle_name?: string;
  email: string;
  phone?: string;
  is_active: boolean;
}

const generateLogin = () =>
  globalThis.crypto?.randomUUID?.() ?? `user-${Math.random().toString(36).slice(2, 10)}`;

const AdminPage = () => {
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const importUsers = useImportOracleUsers();
  const createUser = useCreateAdminUser();
  const deleteUser = useDeleteDirectoryUser();
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];

  const [nameFilter, setNameFilter] = useState("");
  const [contactFilter, setContactFilter] = useState("");
  const [objectFilter, setObjectFilter] = useState<ObjectSelectValue>();
  const [previewUser, setPreviewUser] = useState<AdminUser | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSeedId, setCreateSeedId] = useState(() => generateLogin());
  const [createForm] = Form.useForm<CreateUserFormValues>();
  const createCopyId = Form.useWatch("id", createForm) ?? createSeedId;
  const [mailOpen, setMailOpen] = useState(false);
  const [mailRecipient, setMailRecipient] = useState<string | undefined>();
  const [mailDefaults, setMailDefaults] = useState<{ subject: string; body: string }>({
    subject: "",
    body: "",
  });
  const [mailTelegram, setMailTelegram] = useState<{
    id?: number;
    username?: string;
    userId?: string;
  }>({});
  const [focusMode, setFocusMode] = useState(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const filters = useMemo<AdminUserFilters>(
    () => ({
      name: nameFilter.trim() || undefined,
      contact: contactFilter.trim() || undefined,
      object: objectFilter
        ? { regionId: objectFilter.regionId, objectId: objectFilter.objectId }
        : undefined,
    }),
    [contactFilter, nameFilter, objectFilter],
  );

  const usersQuery = useAdminUsers(filters);
  const users = usersQuery.data ?? [];
  const pagination = useStandardPagination(users.length);
  const tableScroll = { x: 1200 };

  const handleOpenSend = useCallback((user: AdminUser) => {
    const fullName = [user.last_name, user.first_name, user.middle_name].filter(Boolean).join(" ");
    const recipient = getPrimaryContact(user, "email") ?? undefined;
    setMailRecipient(recipient);
    setMailDefaults({
      subject: `AC Web — сообщение для ${fullName || user.id}`,
      body: `Здравствуйте, ${fullName || "коллеги"}!\n\n`,
    });
    setMailTelegram({
      id: user.telegram_id ?? undefined,
      username: user.telegram_username ?? undefined,
      userId: user.id,
    });
    setMailOpen(true);
  }, []);

  const canSend = useCallback(
    (user: AdminUser) => Boolean(getPrimaryContact(user, "email") || user.telegram_id),
    [],
  );

  const columns: ColumnsType<AdminUser> = useMemo(
    () => [
      { title: "Логин", dataIndex: "id", width: 160 },
      {
        title: "ФИО",
        dataIndex: "fio",
        render: (_value, record) =>
          [record.last_name, record.first_name, record.middle_name].filter(Boolean).join(" ") ||
          "—",
      },
      {
        title: "Email",
        dataIndex: "email",
        render: (_value, record) => getPrimaryContact(record, "email") ?? "—",
      },
      {
        title: "Телефон",
        dataIndex: "phone",
        render: (_value, record) => getPrimaryContact(record, "phone") ?? "—",
      },
      {
        title: "Компания",
        dataIndex: "company",
        render: (value?: string) => value || "—",
        ellipsis: true,
      },
      {
        title: "Город",
        dataIndex: "city",
        render: (value?: string) => value || "—",
      },
      {
        title: "Статус",
        dataIndex: "status",
        render: (value?: string) => {
          const v = (value ?? "").toLowerCase();
          const map: Record<string, { color: string; label: string }> = {
            active: { color: "green", label: "Активен" },
            pending: { color: "blue", label: "Ожидает" },
            blocked: { color: "red", label: "Блокирован" },
          };
          const item = map[v] ?? { color: "default", label: v || "—" };
          return <Tag color={item.color}>{item.label}</Tag>;
        },
      },
      {
        title: "Последний вход",
        dataIndex: "last_login_at",
        render: (value?: string) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "—"),
      },
      {
        title: "Действия",
        dataIndex: "actions",
        width: 140,
        render: (_value, record) => (
          <Space>
            <Button
              size="small"
              type="text"
              icon={<MailOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                handleOpenSend(record);
              }}
              disabled={!canSend(record)}
            />
            <Button
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/admin/users/${record.id}`);
              }}
            >
              Настроить
            </Button>
          </Space>
        ),
      },
    ],
    [navigate, handleOpenSend, canSend],
  );

  const handleImport = async () => {
    try {
      const result = await importUsers.mutateAsync();
      message.success(
        `Импорт завершён: добавлено ${result.users_inserted} пользователей и ${result.contacts_inserted} контактов.`,
      );
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  const resetFilters = () => {
    setNameFilter("");
    setContactFilter("");
    setObjectFilter(undefined);
  };

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setFocusMode(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const popupContainer = useCallback(
    (node?: HTMLElement) => overlayRef.current ?? node?.parentElement ?? document.body,
    [],
  );

  const handleOpenCreate = () => {
    setCreateSeedId(generateLogin());
    setCreateOpen(true);
  };

  const handleCreate = async (values: CreateUserFormValues) => {
    try {
      const email = values.email.trim().toLowerCase();
      const phone = values.phone?.trim() || undefined;
      await createUser.mutateAsync({
        data: {
          id: values.id.trim(),
          last_name: values.last_name?.trim() || undefined,
          first_name: values.first_name?.trim() || undefined,
          middle_name: values.middle_name?.trim() || undefined,
          is_active: values.is_active,
          status: values.is_active ? "active" : "blocked",
          role: "user",
          contacts: [
            { type: "email", value: email, is_primary: true },
            ...(phone ? [{ type: "phone", value: phone, is_primary: false }] : []),
          ],
          objects: [],
        },
      });
      message.success("Пользователь создан");
      setCreateOpen(false);
      navigate(`/admin/users/${values.id.trim()}`);
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  const handleDelete = async (user: AdminUser) => {
    try {
      await deleteUser.mutateAsync({ id: user.id });
      message.success("Пользователь удален");
      setPreviewUser(null);
    } catch (err) {
      message.error(extractErrorMessage(err));
    }
  };

  return (
    <PageLayoutWithHelp
      title="Пользователи панели"
      extra={
        <Space>
          <Button type="primary" onClick={handleOpenCreate}>
            Добавить пользователя
          </Button>
          <Button onClick={() => navigate("/admin/users/new")}>Расширенная форма</Button>
        </Space>
      }
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          После миграции из БД управление выполняется только через этот интерфейс.
        </Typography.Text>

        <Alert
          type="warning"
          showIcon
          title="Импорт пользователей из БД"
          description="Процедура нужна для первоначального копирования учётных записей. После импорта ведите справочник пользователей только в системе."
        />
        <Card>
          <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
            <Typography.Text>
              Импорт добавляет только отсутствующие логины и их контакты (email/телефон). Операция
              безопасна, её можно запускать повторно.
            </Typography.Text>
            <Popconfirm
              title="Импортировать пользователей из БД?"
              description="Будут добавлены только новые логины и контакты. Текущие записи не изменяются."
              onConfirm={handleImport}
            >
              <Button type="primary" loading={importUsers.isPending}>
                Импорт из БД
              </Button>
            </Popconfirm>
            {importUsers.data ? (
              <Typography.Text type="success">
                В последнем запуске добавлено {importUsers.data.users_inserted} пользователей и{" "}
                {importUsers.data.contacts_inserted} контактов.
              </Typography.Text>
            ) : null}
          </Space>
        </Card>

        <FilterBar>
          <FilterRow style={{ width: "100%" }}>
            <Input
              allowClear
              placeholder="Имя или фамилия"
              value={nameFilter}
              onChange={(event) => setNameFilter(event.target.value)}
              style={{ minWidth: 220, flex: 1 }}
            />
            <Input
              allowClear
              placeholder="Телефон или email"
              value={contactFilter}
              onChange={(event) => setContactFilter(event.target.value)}
              style={{ minWidth: 220, flex: 1 }}
            />
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={objectFilter}
              onChange={setObjectFilter}
              placeholder="Ответственные по объекту"
              allowClear
              style={{ minWidth: 320, flex: 1 }}
            />
            <Button onClick={resetFilters}>Сбросить</Button>
          </FilterRow>
        </FilterBar>

        <ConfigProvider getPopupContainer={popupContainer} popupMatchSelectWidth={false}>
          <EnterpriseTable
            rowKey="id"
            dataSource={users}
            loading={usersQuery.isLoading || usersQuery.isFetching}
            columns={columns}
            pagination={pagination}
            size="small"
            scroll={tableScroll}
            onRow={(record) => ({
              onClick: () => setPreviewUser(record),
            })}
            toolbar={{
              reload: true,
              onReload: () => usersQuery.refetch(),
              extra: (
                <Tooltip title={focusMode ? "Выход из фокуса" : "Фокус на таблице"}>
                  <Button
                    type="text"
                    icon={focusMode ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    onClick={() => setFocusMode((prev) => !prev)}
                  />
                </Tooltip>
              ),
            }}
          />
        </ConfigProvider>

        <DirectoryUserPreview
          open={Boolean(previewUser)}
          user={previewUser ?? undefined}
          onClose={() => setPreviewUser(null)}
          onEdit={(user) => navigate(`/admin/users/${user.id}`)}
          onDelete={handleDelete}
          deleting={deleteUser.isPending}
        />

        <MailModal
          open={mailOpen}
          onClose={() => setMailOpen(false)}
          defaultEmail={mailRecipient}
          defaultTelegramId={mailTelegram.id}
          defaultTelegramUsername={mailTelegram.username}
          defaultUserId={mailTelegram.userId}
          defaultSubject={mailDefaults.subject}
          defaultBody={mailDefaults.body}
        />

        <DrawerForm<CreateUserFormValues>
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          title="Новый пользователь"
          size={520}
          onSubmit={handleCreate}
          submitText="Создать"
          cancelText="Отмена"
          showReset
          loading={createUser.isPending}
          form={createForm}
          copyUrl={`/admin/users/${encodeURIComponent(createCopyId)}`}
          initialValues={{
            id: createSeedId,
            email: "",
            is_active: true,
          }}
        >
          <Form.Item
            name="id"
            label="Логин"
            tooltip="Логин генерируется автоматически"
            rules={[{ required: true, message: "Логин обязателен" }]}
          >
            <Input />
          </Form.Item>
          <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              name="last_name"
              label="Фамилия"
              style={{ flex: 1, minWidth: 200 }}
              rules={[{ required: true, message: "Укажите фамилию" }]}
            >
              <Input placeholder="Иванов" />
            </Form.Item>
            <Form.Item
              name="first_name"
              label="Имя"
              style={{ flex: 1, minWidth: 200 }}
              rules={[{ required: true, message: "Укажите имя" }]}
            >
              <Input placeholder="Иван" />
            </Form.Item>
            <Form.Item name="middle_name" label="Отчество" style={{ flex: 1, minWidth: 200 }}>
              <Input placeholder="Иванович" />
            </Form.Item>
          </Space>
          <Space orientation="horizontal" size="middle" style={{ width: "100%" }} wrap>
            <Form.Item
              name="email"
              label="Email"
              style={{ flex: 1, minWidth: 240 }}
              rules={[
                { required: true, message: "Укажите email" },
                { type: "email", message: "Укажите корректный email" },
              ]}
            >
              <Input placeholder="user@example.com" type="email" />
            </Form.Item>
            <Form.Item name="phone" label="Телефон" style={{ flex: 1, minWidth: 200 }}>
              <Input placeholder="+7 (900) 000-00-00" />
            </Form.Item>
          </Space>
          <Form.Item name="is_active" label="Доступ" valuePropName="checked">
            <Switch checkedChildren="Активен" unCheckedChildren="Выключен" />
          </Form.Item>
        </DrawerForm>

        {focusMode ? (
          <FocusOverlay title="Пользователи панели" containerRef={overlayRef}>
            <ConfigProvider getPopupContainer={popupContainer} popupMatchSelectWidth={false}>
              <EnterpriseTable
                rowKey="id"
                dataSource={users}
                loading={usersQuery.isLoading || usersQuery.isFetching}
                columns={columns}
                pagination={pagination}
                size="small"
                scroll={tableScroll}
                onRow={(record) => ({
                  onClick: () => setPreviewUser(record),
                })}
                toolbar={{
                  reload: true,
                  onReload: () => usersQuery.refetch(),
                  extra: (
                    <Tooltip title="Выход из фокуса">
                      <Button
                        type="text"
                        icon={<FullscreenExitOutlined />}
                        onClick={() => setFocusMode(false)}
                      />
                    </Tooltip>
                  ),
                }}
              />
            </ConfigProvider>
          </FocusOverlay>
        ) : null}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default AdminPage;
